import type {Component} from 'typings/portaro.fe.types';
import type {ModalWindowBindings, ModalWindowResolveValues} from '../conversation';
import type {IAugmentedJQuery, IOnInit, ITimeoutService} from 'angular';
import type {User, UserPreference} from 'typings/portaro.be.types';
import {replaceAllByProperty} from 'shared/utils/array-utils.fp';
import type {UserPreferencesService} from '../../../user/user-preferences/user-preferences.service';
import KpValueEditor from '../../value-editors/kp-value-editor/KpValueEditor.svelte';
import type {ValueEditorLabelContext} from '../../value-editors/label/value-editor-label-context';
import {createValueEditorsLabelContext, LABEL_CONTEXT_KEY} from '../../value-editors/label/value-editor-label-context';
import KpDatatypedValue from '../../components/kp-datatyped/KpDatatypedValue.svelte';


export interface UserPreferencesModalModel {
    userPreferences: UserPreference[];
    user: User;
    message: string;
}

export default class UserPreferencesModalComponent implements Component<ModalWindowBindings<UserPreferencesModalModel>> {
    public static componentName = 'userPreferencesModal';

    public bindings = {
        resolve: '<'
    } as const;
    public controller = UserPreferencesModalController;
    public templateUrl = require('./user-preferences-modal.tpl.ftl');
}

class UserPreferencesModalController implements ModalWindowBindings<UserPreferencesModalModel>, IOnInit {
    public static controllerName = 'userPreferencesModalController';

    public resolve: ModalWindowResolveValues<UserPreferencesModalModel>;
    public kpValueEditorComponent = KpValueEditor;
    public kpDatatypedValue = KpDatatypedValue;

    public userPreferences: UserPreference[];
    public user: User;
    public message: string;

    /*@ngInject*/
    constructor(private $element: IAugmentedJQuery,
                private $timeout: ITimeoutService,
                private userPreferencesService: UserPreferencesService) {
    }

    public $onInit() {
        this.user = this.resolve.model.user;
        this.userPreferences = this.resolve.model.userPreferences;
        this.message = this.resolve.model.message;
    }

    public close() {
        this.resolve.modalWindowActions.acknowledge();
    }

    public async save(preference: UserPreference) {
        const savedPreference = await this.userPreferencesService.save(preference);
        replaceAllByProperty('keyId', savedPreference)(this.userPreferences);
    }

    public async setToDefault(preference: UserPreference) {
        const savedPreference = await this.userPreferencesService.setToDefault(preference);
        replaceAllByProperty('keyId', savedPreference)(this.userPreferences);
    }

    public async togglePreferenceEditing(preference: UserPreference) {
        if (preference.state === 'NORMAL') {
            preference.state = 'EDITING';
            await this.$timeout(); // just like` await tick()` in svelte
            this.focusPreferenceEditForm(preference);
        } else {
            preference.state = 'NORMAL';
        }
    }

    public createPreferenceEditorLabelContext(preference: UserPreference): ValueEditorLabelContext {
        return createValueEditorsLabelContext(this.generateEditorIdForPreference(preference), preference.keyText);
    }

    public setPreferenceEditorLabelContext(preferenceEditorLabelContext: ValueEditorLabelContext): Map<string, any> {
        return new Map([[LABEL_CONTEXT_KEY, preferenceEditorLabelContext]]);
    }

    private generateEditorIdForPreference(preference: UserPreference): string {
        return `user-preference-${preference.keyId}-editor`;
    }

    private focusPreferenceEditForm(preference: UserPreference) {
        const element = this.$element[0].querySelector<HTMLElement>(`#user-preference-${preference.keyId}-form`);
        element.focus();
    }
}